import { FormHelperText, Divider } from "@material-ui/core";
import Select from "../../../../../../common/StyledSelect";
import React from "react";
import CustomTextField from "../../common/CustomTextField";
import { withStyles } from "@material-ui/core/styles";
import StopDetailsTitle from "../../common/StopDetailsTitle";
import { formatAddress } from "../../../../utils/utils";
import { isPaymentDetailsRequired } from "./../Payment/util";
import ShipmentDetails from "../../common/ShipmentDetails";
import constants from "../constants";
import appConstants from "../../../../../../../appConstants";
import PaymentDetailsSummary from "./../Payment/PaymentDetailsSummary";
import ExceptionDetails from "./ExceptionDetails";
import { getShipmentFreightBill } from "../util";
import { MAX_LENGTH_CONSTRAINTS } from "../../common/constants";

const styles = (theme) => ({
  receiverFormControl: {
    marginTop: "16px",
    display: "flex",
    width: "100%",
    flexDirection: "column",
  },
  receiverDetails: {
    fontSize: 10,
    color: theme.TextColor,
    opacity: 0.6,
    marginTop: "20px",
  },
  flexRow: {
    display: "flex",
    direction: "column",
    "&>div": {
      width: "60%",
    },
  },
  scacCode: {
    width: "70%"
  },
  labelProps: {
    fontSize: theme.textFontSize,
    fontWeight: 500,
  },
  selectOption: {
    padding: "2px 10px",
    color: theme.recommendationCardStatusTextColor,
  },
  formHelperText: {
    marginTop: 0,
    marginBottom: 16,
    color: theme.TextColor,
  },
  inputWidth: {
    width: "60%",
  },
  scacCodeInput: {
    width: "40%",
  },
  divider: {
    backgroundColor: theme.gridIconBorderColor,
    opacity: 0.6,
    marginTop: 8,
  },
});

const getReceiverInputLabelDetails = (isAgent) => {
  if (isAgent) {
    return {
      sectionHeaderLabel: "AGENT DETAILS",
      name: "Agent Name",
      email: "Email",
      phone: "Phone Number",
    };
  }
  return {
    sectionHeaderLabel: "RECEIVER DETAILS",
    name: "Receiver's Name",
    email: "Receiver's Email",
    phone: "Receiver's Cell Phone",
  };
};

const ReceiverDetails = ({
  classes,
  receiverDetailsInfo,
  receiverDetailsErrorInfo,
  handleChange,
  handleReceiverName,
  routeData,
  stopData,
  shipmentList,
  isAgent,
  customerReceiverList,
  freightBillData,
  scacCodeOptions,
}) => {
  const { name, email, phone, sectionHeaderLabel } =
    getReceiverInputLabelDetails(isAgent);
  return (
    <>
      {!isAgent && (
        <StopDetailsTitle
          stopName={stopData.name}
          stopAddress={formatAddress({
            address: stopData?.address,
            address2: stopData?.address2,
            city: stopData?.city,
            state: stopData?.state,
            zip: stopData?.zip,
          })}
          driverNumber={routeData?.driverNumber}
        />
      )}
      {shipmentList.map(({ editProData, shipmentData }) => {
        const { payment, exception, deliveryCode } = editProData;
        return (
          <>
            <ShipmentDetails
              shipmentData={{ ...shipmentData, ...editProData }}
              freightBillDetails={getShipmentFreightBill(
                freightBillData,
                shipmentData.proId
              )}
            />
            {deliveryCode === appConstants.DELIVERY_CODE.DE && (
              <ExceptionDetails exceptionDetails={exception} />
            )}
            {payment &&
              isPaymentDetailsRequired({
                deliveryStatus: editProData.deliveryCode,
                codAmount: shipmentData?.codAmount,
                freightAmount: shipmentData?.freightAmount
              }) && (
                <PaymentDetailsSummary
                  data={{
                    freightAmount: shipmentData?.freightAmount,
                    codAmount: shipmentData?.codAmount,
                    freightPaymentType: payment.freightPaymentType,
                    freightCheckNumber: payment.freightCheckNumber,
                    freightAmountCollected: payment.freightAmount,
                    codPaymentType: payment.codPaymentType,
                    codCheckNumber: payment.codCheckNumber,
                    codAmountCollected: payment.codAmount
                  }}
                />
              )}
          </>
        );
      })}
      <div className={classes.receiverFormControl}>
        <p className={classes.receiverDetails}>{sectionHeaderLabel}</p>
        <div className={classes.flexRow}>
          {isAgent ? (
            <CustomTextField
              id="name"
              handleChange={handleChange("receiverName")}
              value={receiverDetailsInfo?.receiverName ?? ""}
              className={classes.inputWidth}
              label={name}
              error={Boolean(receiverDetailsErrorInfo.receiverName)}
              helperText={receiverDetailsErrorInfo.receiverName}
              inputProps={{
                maxLength: MAX_LENGTH_CONSTRAINTS.DELIVERY_PRO_FORM.RECEIVER_NAME,
              }}
            />
          ) : (
            <Select
              textFieldProps={{
                id: "receiverName",
                label: name,
                variant: "outlined",
                helperText: receiverDetailsErrorInfo.receiverName,
              }}
              value={receiverDetailsInfo?.receiverName}
              error={Boolean(receiverDetailsErrorInfo.receiverName)}
              onChange={handleReceiverName}
              data={customerReceiverList}
              canAcceptNewValue
              newValueParams={constants.RECEIVER_NAME_PARAMS}
              createLabel={`Add ${receiverDetailsInfo?.receiverName ?? ""}`}
            />
          )}
        </div>
        <CustomTextField
          id="email"
          handleChange={handleChange("receiverEmail")}
          value={receiverDetailsInfo?.receiverEmail ?? ""}
          className={classes.inputWidth}
          label={email}
          error={Boolean(receiverDetailsErrorInfo.receiverEmail)}
          helperText={receiverDetailsErrorInfo.receiverEmail}
          inputProps={{
            maxLength: MAX_LENGTH_CONSTRAINTS.DELIVERY_PRO_FORM.RECEIVER_EMAIL,
          }}
        />
        {!isAgent && (
          <FormHelperText className={classes.formHelperText}>
            Fill to send receipt copy via email
          </FormHelperText>
        )}
        <CustomTextField
          id="phone"
          handleChange={handleChange("receiverCellPhone")}
          value={receiverDetailsInfo?.receiverCellPhone ?? ""}
          className={classes.inputWidth}
          label={phone}
          error={Boolean(receiverDetailsErrorInfo.receiverCellPhone)}
          helperText={receiverDetailsErrorInfo.receiverCellPhone}
          mask={constants.PHONE_NUMBER_MASK}
        />
        {!isAgent && (
          <FormHelperText className={classes.formHelperText}>
            Fill to receive receipt copy via SMS
          </FormHelperText>
        )}
        {isAgent && (
          <>
            <Divider className={classes.divider} />
            <div className={`${classes.flexRow} ${classes.scacCode}`}>
              <Select
                textFieldProps={{
                  id: "scacCode",
                  label: "SCAC CODE",
                  variant: "outlined",
                  helperText: receiverDetailsErrorInfo.scacCode,
                }}
                className={classes.scacCodeInput}
                error={Boolean(receiverDetailsErrorInfo.scacCode)}
                value={receiverDetailsInfo?.scacCode}
                onChange={handleChange("scacCode")}
                data={scacCodeOptions}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default withStyles(styles)(ReceiverDetails);
